import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getActivitiesList, getActivitiesStatusList } from '../../../../api/activities';
import { GET_ALL_QUERY_PARAMS, InspectionStatuses } from '../../../../constants';
import {
  setComments,
  setInspectionsTypeData,
  setPermitUsers,
  setProject,
  setSelectedInspection,
  setStatusData,
  setTitlesData,
  setTrades,
} from '../../../../redux/slices/inspectionsSlice';
import { RootState } from '../../../../redux/root';
import store from '../../../../redux/store';
import { clearTemplateTexts } from '../../../../organisms/ThresholdReports/docOvelayTool';
import { formFieldMappingsOf } from '../../../../organisms/ThresholdReports/PreviewTool/builder/staticFieldOptions';
import { getInspectionTypesCustom } from '../../../../api/inspectionTypes';
import { getProjectByID } from '../../../../api/projects';
import {
  InspectionsTypeList,
  createPermitInspectionsComment,
  getInspectionFormFieldValues,
  getPermitInspectionById,
  getPermitInspectionsComments,
} from '../../../../api/inspections';
import { getPermitUsers, getUsersCustom } from '../../../../api/users';
import { USER_TYPES_VALUES } from '../../../../constants/forms';
import { setAllEmployees } from '../../../../redux/slices/generalSlice';

const useLoadInspectionEditor = ({ selectedTitle }) => {
  const dispatch = useDispatch();
  const allEmployees = useSelector((s: RootState) => s.general.allEmployees);
  const { titlesData, selectedInspection, project, isSystemMessages, newComment } = useSelector((s: RootState) => s.inspections);
  const selectedForm = useSelector((s: RootState) => s.thresholdForms.selectedForm);

  // Loaders resolve after several other dispatches have landed, so they must
  // merge into the CURRENT inspection rather than the copy their closure
  // captured — otherwise a slow response resurrects stale sibling fields.
  const selectedInspectionRef = useRef(selectedInspection);
  selectedInspectionRef.current = selectedInspection;

  // The editor opens instantly but every dropdown/value below is filled by the
  // requests in this hook. Without a busy flag the inspector sees a form that
  // looks ready but still has empty selects. `isLoading` starts `true` and only
  // drops once nothing is in flight any more.
  const pendingRef = useRef(0);
  const settleTimerRef = useRef<any>(null);
  const loadedIdRef = useRef<any>(undefined);
  const initialDoneRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);

  const syncLoading = () => {
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
    // Only the first pass for a given inspection is worth covering. Later
    // fetches are user-triggered (picking another activity title reloads the
    // status list, toggling system messages reloads comments) — throwing the
    // overlay back up there would blank the form under the inspector's cursor.
    if (initialDoneRef.current) return;
    if (pendingRef.current > 0) {
      setIsLoading(true);
      return;
    }
    // Several loads are chained through effects (project -> inspection types,
    // activity title -> statuses), so the in-flight count legitimately touches
    // zero mid-sequence. Wait a beat before declaring the editor ready,
    // otherwise the spinner flickers off and back on.
    settleTimerRef.current = setTimeout(() => {
      initialDoneRef.current = true;
      setIsLoading(false);
    }, 250);
  };

  // Wraps a loader so it counts toward `isLoading`. Failures are swallowed on
  // purpose — an API error must still clear the spinner, or the editor would be
  // stuck behind it forever.
  const track = (load: () => Promise<any>) => {
    pendingRef.current += 1;
    syncLoading();
    return Promise.resolve()
      .then(load)
      .catch(() => undefined)
      .finally(() => {
        pendingRef.current -= 1;
        syncLoading();
      });
  };

  useEffect(() => {
    if (titlesData.length === 0) {
      track(loadActivityList);
    }
    if (allEmployees.length === 0) {
      track(loadAllEmployees);
    }
  }, []);

  useEffect(() => {
    // Opening a different inspection (the editor is reused) starts a fresh
    // load, so put the overlay back. A plain `isSystemMessages` toggle must not.
    if (loadedIdRef.current !== selectedInspection.id) {
      loadedIdRef.current = selectedInspection.id;
      initialDoneRef.current = false;
      setIsLoading(true);
    }
    if (selectedInspection.projectId) {
      track(() => loadProject(selectedInspection.projectId));
    }
    if (selectedInspection.id) {
      track(() => loadInspectionTrades(selectedInspection.id));
      track(() => loadComments(selectedInspection.id));
    }
    if (selectedInspection.permit) {
      track(() => loadPermitUsers(selectedInspection.permit));
    }
    // Nothing to fetch for this inspection? Release the overlay anyway.
    syncLoading();
  }, [selectedInspection.id, isSystemMessages]);

  // Static fields mapped to a Regular Forms field ("form:..." mappings) can
  // only be resolved by the server. Fetched whenever the inspection or the
  // matched form changes; `formFieldsKeyRef` stops the re-renders in between
  // from asking again for the same set.
  const formFieldsKeyRef = useRef('');
  useEffect(() => {
    const fields = formFieldMappingsOf(selectedForm?.formMarkup);
    const { id } = selectedInspection;
    if (!id || fields.length === 0) return;
    const key = `${id}|${fields.join('|')}`;
    if (formFieldsKeyRef.current === key) return;
    formFieldsKeyRef.current = key;
    track(() => loadFormFieldValues(id, fields));
  }, [selectedInspection.id, selectedForm]);

  useEffect(() => {
    if (project.jurisdiction) {
      track(() => loadInspectionTypes(project.jurisdiction));
    }
  }, [project.id]);

  useEffect(() => {
    if (selectedTitle) {
      track(loadStatusData);
    }
  }, [selectedTitle]);

  // Declared last so it runs after the effects above have registered their
  // requests: with nothing to fetch it releases the initial `true`.
  useEffect(() => {
    syncLoading();
    return () => {
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
      }
    };
  }, []);

  const loadActivityList = async () => {
    const results = await getActivitiesList({
      ...GET_ALL_QUERY_PARAMS,
      activityType: 8,
    });
    const activitiesTitles = results.data.results?.filter(title => title?.isActiveStatus === 'True');
    dispatch(setTitlesData(activitiesTitles));
  };

  const loadInspectionTypes = async jurisdiction => {
    const results = await getInspectionTypesCustom(jurisdiction);
    // @ts-ignore
    dispatch(setInspectionsTypeData(results.data));
  };

  const loadProject = async projectId => {
    const results = await getProjectByID(projectId);
    dispatch(setProject(results.data));
  };

  const loadStatusData = async () => {
    if (selectedTitle) {
      const activitiesStatusList = await getActivitiesStatusList({
        ...GET_ALL_QUERY_PARAMS,
        activity: selectedTitle.id,
      });
      const activitiesStatus = activitiesStatusList.data.results?.filter(status => {
        if (status?.isActiveStatus === 'True') {
          switch (status.name) {
            case InspectionStatuses.BY_THRESHOLD:
            case InspectionStatuses.NOT_SCHEDULED:
            case InspectionStatuses.READY_FOR_SCHEDULING:
              return false;
            default:
              return true;
          }
        }
        return false;
      });
      dispatch(setStatusData(activitiesStatus));
    }
  };

  const loadInspectionTrades = async inspectionId => {
    const result = await getPermitInspectionById({
      ...GET_ALL_QUERY_PARAMS,
      id: inspectionId,
    });
    dispatch(setTrades(result.data));

    // The editor is opened by handing it a ROW out of a cached inspection list
    // (getPendingInspections keeps its data for ~1000 hours), so that row's
    // formMarkup can be older than what Inspection Notes autosaved. This request
    // already fetches the authoritative record — take its markup too, or a
    // stale copy gets rendered and then written back over the good one by
    // InspectionTabs, losing the inspector's notes for real.
    //
    // Guarded on presence: a record with no markup yet must not clobber the
    // template that InspectionTabs may have just seeded.
    if (result.data && result.data.formMarkup) {
      // Authoritative ANSWERS, rebased onto the CURRENT template — not used
      // raw. Dispatching the saved copy verbatim overwrote the rebase that
      // InspectionTabs had just done, so any field an admin added to the form
      // AFTER this inspection was created never appeared: the saved markup
      // predates it, and this was the last write to win.
      //
      // selectedForm is loaded by a separate (large, slow) query. When it has
      // not arrived yet the saved copy is used as-is and InspectionTabs rebases
      // once it does, so both orderings end up in the same place.
      const form = store.getState().thresholdForms.selectedForm;
      const rebased = form && form.formMarkup
        ? clearTemplateTexts(form.formMarkup, {
          ...selectedInspectionRef.current,
          formMarkup: result.data.formMarkup,
        })
        : result.data.formMarkup;

      dispatch(
        setSelectedInspection({
          ...selectedInspectionRef.current,
          formMarkup: rebased,
        }),
      );
    }
  };

  const loadFormFieldValues = async (inspectionId, fields: string[]) => {
    let result;
    try {
      result = await getInspectionFormFieldValues(inspectionId, fields);
    } catch (e) {
      // Let the next pass ask again rather than caching a failure.
      formFieldsKeyRef.current = '';
      throw e;
    }
    const formFieldValues = {};
    (result.data?.values || []).forEach(({ field, value }) => {
      formFieldValues[field] = value;
    });

    const { current } = selectedInspectionRef;
    // The inspector moved on to another inspection while this was in flight.
    if (current.id !== inspectionId) return;
    const withValues = { ...current, formFieldValues };

    // Static values are baked into the markup by clearTemplateTexts, so
    // re-run it now that they exist. It rebases the saved answers, and a static
    // field always takes the freshly resolved value, so nothing typed is lost.
    const form = store.getState().thresholdForms.selectedForm;
    dispatch(
      setSelectedInspection({
        ...withValues,
        formMarkup: form && form.formMarkup
          ? clearTemplateTexts(form.formMarkup, withValues)
          : current.formMarkup,
      }),
    );
  };

  const loadPermitUsers = async permitId => {
    const results = await getPermitUsers({
      permit_id: permitId,
      userRole: USER_TYPES_VALUES.CLIENT,
      exclude_companies: true,
    });
    dispatch(setPermitUsers(results.data));
  };

  const loadAllEmployees = async () => {
    const employeeList = await getUsersCustom({
      userRole: USER_TYPES_VALUES.EMPLOYEE,
    });

    // @ts-ignore
    const allEmployees = employeeList.data.filter(employee => employee?.isActive);
    dispatch(setAllEmployees(allEmployees));
  };

  const loadComments = async id => {
    const comments = await getPermitInspectionsComments({ ...GET_ALL_QUERY_PARAMS, system_message: isSystemMessages }, id);
    dispatch(setComments(comments.data.results));
  };

  return { isLoading };
};

export default useLoadInspectionEditor;
